import React, { useState } from 'react';

import Video from '../components/video';
import Metric from '../components/metric';
import QuoteCarouselLegacy from '../components/quote-carousel-legacy';
import ImageWithCaption from '../components/image-with-caption';
import ImageWithFloatingText from '../components/image-with-floating-text';
import MultiImageSection from '../components/multi-image-section';
import InsightCTA from '../components/insight-cta';
import Quote from '../models/Quote/Quote';
import HighlightOverlay from '../components/highlight-overlay';

/**
 * Rich-renderer function that can be consumed by React-Rich-Text render in order
 * to generate a styled element based on a particular rich text node, and it's
 * children
 *
 * @function
 * @param {object} node rich-text node
 */
const RichEntry = (node, geoSpace) => {
  let content = null;
  const entryType = node?.internal?.type;

  if (entryType === 'ContentfulVideo') {
    const { embedUrl, title } = node;

    content = <Video title={title} embedUrl={embedUrl} />;
  }

  if (entryType === 'ContentfulMetric') {
    const { title, percentage } = node;

    content = <Metric title={title} percentage={percentage} />;
  }

  if (entryType === 'ContentfulQuoteBlock') {
    const embeddedQuotes = [];
    const { author, authorSubtitle, quote, client, image } = node;
    const singleQuote = {
      author: author,
      authorSubtitle: authorSubtitle,
      quote: quote,
      client: client,
      image: image
    };
    embeddedQuotes.push(new Quote(singleQuote));
    content = <QuoteCarouselLegacy quotes={embeddedQuotes} />;
  }

  if (entryType === 'ContentfulImageWithCaption') {
    const { linkUrl, linkText, caption, captionAlignment } = node;
    const image = node?.image?.url;
    const alt = node?.image?.title;
    const alignment = captionAlignment;

    const imageWithCaptionHighlight = () => {
      // state to turn on and off the highlight overlay
      const [isOpen, setIsOpen] = useState(false);

      return (
        <>
          <ImageWithCaption
            onClickHandler={() => setIsOpen(true)}
            {...{ image, linkUrl, alt, caption, alignment, linkText }}
          />
          <HighlightOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} image={image} alt={alt} />
        </>
      );
    };

    content = imageWithCaptionHighlight();
  }

  if (entryType === 'ContentfulImageWithFloatingText') {
    const { imageAlignment, richBody } = node;
    const image = node?.image?.gatsbyImageData;
    const url = node?.image?.url;
    const alt = node?.image?.title;
    const richText = richBody;

    const imageWithFloatingTextHighlight = () => {
      // state to turn on and off the highlight overlay
      const [isOpen, setIsOpen] = useState(false);

      return (
        <>
          <ImageWithFloatingText
            onClickHandler={() => setIsOpen(true)}
            geoSpace={geoSpace}
            {...{ image, alt, imageAlignment, richText }}
          />
          <HighlightOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} image={url} alt={alt} />
        </>
      );
    };

    content = imageWithFloatingTextHighlight();
  }

  if (entryType === 'ContentfulMultiImageSection') {
    const multiImageSectionHighlight = () => {
      // state to turn on and off the highlight overlay
      const [isOpen, setIsOpen] = useState(false);
      const [selectedImgSrc, setSelectedImgSrc] = useState('');

      const clickHandler = imgSrc => {
        setIsOpen(true);
        if (imgSrc) {
          setSelectedImgSrc(imgSrc);
        }
      };

      return (
        <>
          <MultiImageSection onClickHandler={src => clickHandler(src)} images={node} />
          <HighlightOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} image={selectedImgSrc} />
        </>
      );
    };
    content = multiImageSectionHighlight();
  }

  if (entryType === 'ContentfulInsightCta') {
    const { caption, buttonText, buttonUrl } = node;

    content = <InsightCTA {...{ caption, buttonText, buttonUrl }} />;
  }

  return content;
};

export default RichEntry;
